/* eslint-disable */
import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useNotificationsStore } from "@/stores/toast_notifications";
 
// Import views
// import Authentication from "@/views/AuthenticationView.vue";
// import Dashboard from "@/views/MainDashboard.vue";
// import UsersDashboard from "@/views/UsersDashboard.vue";
// import ChangePassword from "@/views/ChangePassword.vue";
// import NotFound from "@/views/NotFound.vue";
// import RestorePassword from "@/views/RestorePassword.vue";
// import UnauthorizedView from "@/views/UnauthorizedView.vue";
 
const routes = [
  {
    path: "/",
    redirect: "/auth",
  },
  {
    path: "/auth",
    name: "Authentication",
    component: () => import("@/views/AuthenticationView.vue"),
    meta: { requiresGuest: true, layout: "blank" },
  },
  {
    path: "/restore-password",
    name: "RestorePassword",
    component: () => import("@/views/RestorePassword.vue"),
    props: true,
    meta: { requiresGuest: true, layout: "blank" },
  },
  {
    path: "/dashboard",
    name: "Dashboard",
    component: () => import("@/views/MainDashboard.vue"),
    meta: {
      requiresAuth: true,
      requiresPermissions: ["view_dashboard_main"],
    },
  },
  {
    path: "/users",
    name: "Users",
    component: () => import("@/views/UsersDashboard.vue"),
    meta: {
      requiresAuth: true,
      requiresPermissions: ["view_dashboard_usuarios"],
    },
  },
  {
    path: "/change-password",
    name: "ChangePassword",
    component: () => import("@/views/ChangePassword.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/unauthorized",
    name: "Unauthorized",
    component: () => import("@/views/UnauthorizedView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: () => import("@/views/NotFound.vue"),
  },
];
 
const router = createRouter({
  history: createWebHistory(),
  routes,
});
 
// --- Helper functions ---
async function ensureUserData(authStore) {
  try {
    await authStore.getUserData();
    return true;
  } catch (error) {
    return false;
  }
}
 
function validateUserData(authStore) {
  return !!(
    authStore.userData?.permissions?.length &&
    authStore.userData?.id &&
    Array.isArray(authStore.userData.permissions)
  );
}
 
function getUserPermissionNames(authStore) {
  return authStore.userData?.permissions?.map((p) => p.name) || [];
}
 
function findFirstAccessibleRoute(userPermissions) {
  return router.getRoutes().find((route) =>
    route.meta?.requiresPermissions?.some((p) => userPermissions.includes(p))
  );
}

function validateRoute(userPermissions, routePath) {
  try {
    const route = router.getRoutes().find((route) => route.path === routePath);
    if (!route) return false;
    return route.meta?.requiresPermissions?.some((p) => userPermissions.includes(p));
  } catch (error) {
    return false;
  }
}
 
// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();
  const notificationsStore = useNotificationsStore();
  const isAuthenticated = await authStore.isUserAuthenticated();
 
  // Redirect unauthenticated users
  if (to.meta.requiresAuth && !isAuthenticated) {
    return next("/auth");
  }

  if(!isAuthenticated) {
    return next();
  }
 
  // //  Redirect authenticated users from guest-only pages
  if (to.meta.requiresGuest && isAuthenticated) {
    return next("/dashboard");
  }

  
  const validateData = validateUserData(authStore);
  if(!validateData && !authStore.isLoadingData && !authStore.isDataFetched) {
    if (to.path) {
      authStore.updateOriginalRoute(to.path);
    }
    authStore.changeLoadingData(true);
  }

  if(authStore.isLoadingData) {    
    try {   
      await ensureUserData(authStore);
      const validateAgain = validateUserData(authStore);
      
      if(!validateAgain) throw new Error("Unauthorized");
      const userPermissions = getUserPermissionNames(authStore);
      const accessibleRoute = findFirstAccessibleRoute(userPermissions);
      if(!accessibleRoute?.name) throw new Error("Unauthorized");
      if(authStore.getOriginalRoute ) {
        const isValidRoute = validateRoute(userPermissions, authStore.getOriginalRoute);
        if(isValidRoute) return next(authStore.getOriginalRoute);
      };
      return next(accessibleRoute ? { name: accessibleRoute.name } : "/unauthorized");
    } catch (error) {     
      return next("/unauthorized");
    }
    finally {
      authStore.changeDataFetched(true);
      authStore.changeLoadingData(false);
      authStore.updateOriginalRoute(null);
    }
  }
  
  next();
});
 
export default router;